import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '../Button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { setPatientDetails } from '../../redux/slices/appointmentSlice';
import { DobField } from '../ui/DobField';
import { formatSsnInput, getInsuranceFieldRules, isValidSsn } from '../../utils/helpers';

const SSN_ERROR_MESSAGE = 'SSN must be 9 digits';

interface InsuranceModalProps {
  isOpen: boolean;
  onClose: () => void;
  isComplete: boolean
}

export const InsuranceModal: React.FC<InsuranceModalProps> = ({
  isOpen,
  onClose,
  isComplete,
}) => {
  const dispatch = useDispatch();
  const insuranceSetting = useSelector((state: RootState) => state.auth.practice?.insurance_setting);
  const patientDetails = useSelector((state: RootState) => state.appointment.patientDetails);

  // Local state for form fields
  const [provider, setProvider] = useState(patientDetails.insuranceDetails?.provider || '');
  const [policyHolderName, setPolicyHolderName] = useState(patientDetails.insuranceDetails?.policyHolderName || '');
  const [policyHolderDob, setPolicyHolderDob] = useState(patientDetails.insuranceDetails?.policyHolderDob || '');
  const [policyHolderDobValid, setPolicyHolderDobValid] = useState(true);
  const [memberId, setMemberId] = useState(patientDetails.insuranceDetails?.memberId || '');
  const [policyHolderSsn, setPolicyHolderSsn] = useState(patientDetails.insuranceDetails?.policyHolderSsn || '');
  const [ssnVisible, setSsnVisible] = useState(false);

  // Sync local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProvider(patientDetails.insuranceDetails?.provider || '');
      setPolicyHolderName(patientDetails.insuranceDetails?.policyHolderName || '');
      setPolicyHolderDob(patientDetails.insuranceDetails?.policyHolderDob || '');
      setPolicyHolderDobValid(true);
      setMemberId(patientDetails.insuranceDetails?.memberId || '');
      setPolicyHolderSsn(patientDetails.insuranceDetails?.policyHolderSsn || '');
      setSsnVisible(false);
    }
  }, [isOpen, patientDetails.insuranceDetails]);

  // Field visibility and required state from backend settings
  const { showDob, requireDob, showMemberId, requireMemberId, showSsn, requireSsn } = getInsuranceFieldRules(insuranceSetting);
  const showDisclaimer = insuranceSetting?.display_disclaimer_text ?? true;
  const ssnError = policyHolderSsn && !isValidSsn(policyHolderSsn) ? SSN_ERROR_MESSAGE : '';

  // Disclaimer text from backend
  const disclaimerText = insuranceSetting?.disclaimer_text || '';

  // Validation: Check if all required fields are filled
  const isFormValid = (): boolean => {
    // Provider and Policy Holder Name are always required
    if (!provider.trim() || !policyHolderName.trim()) return false;

    // DOB must be a real date whenever it is filled in, and present when the practice requires it
    if (showDob && ((requireDob && !policyHolderDob) || !policyHolderDobValid)) return false;
    if (showMemberId && requireMemberId && !memberId.trim()) return false;
    if (showSsn && ((requireSsn && !policyHolderSsn) || ssnError)) return false;

    return true;
  };

  const handleSave = () => {
    dispatch(setPatientDetails({
      ...patientDetails,
      insuranceDetails: {
        provider,
        policyHolderName,
        policyHolderDob: showDob ? policyHolderDob : undefined,
        memberId: showMemberId ? memberId : undefined,
        policyHolderSsn: showSsn && policyHolderSsn ? policyHolderSsn : undefined,
      }
    }));
    onClose();
  };

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-full max-w-2xl max-h-[90vh] p-0 rounded-xl overflow-hidden bg-white mx-4 md:mx-auto flex flex-col">
        {/* Header */}
        <div className="p-6 pb-4 flex items-start gap-4 relative shrink-0">
          <div className="bg-gradient-to-r from-teamcare-cyan to-teamcare-cyan-dark p-2 rounded-lg">
            {isComplete ? <ShieldCheck size={24} className='text-white' /> : <Shield size={24} className='text-white' />}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-800">Insurance Information</h3>
            <p className="text-gray-500 text-sm mt-0.5">Please provide your insurance details.</p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
          {/* Insurance Provider - Always required */}
          <div className="form-control w-full">
            <label className="label py-1">
              <span className="label-text font-medium text-gray-700">Insurance Provider <span className="text-red-500">*</span></span>
            </label>
            <input
              type="text"
              placeholder="e.g., Blue Cross Blue Shield"
              className="input input-bordered w-full bg-white border-gray-200 focus:border-teamcare-cyan focus:outline-none"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            />
          </div>

          {/* Policy Holder Name + DOB Row */}
          <div className={`grid grid-cols-1 ${showDob ? 'md:grid-cols-2' : ''} gap-4`}>
            {/* Policy Holder Name - Always required */}
            <div className="form-control w-full">
              <label className="label py-1">
                <span className="label-text font-medium text-gray-700">Policy Holder Name <span className="text-red-500">*</span></span>
              </label>
              <input
                type="text"
                placeholder="Primary policy holder name"
                className="input input-bordered w-full bg-white border-gray-200 focus:border-teamcare-cyan focus:outline-none"
                value={policyHolderName}
                onChange={(e) => setPolicyHolderName(e.target.value)}
              />
            </div>

            {/* Policy Holder DOB - Conditional */}
            {showDob && (
              <div className="form-control w-full">
                <label className="label py-1">
                  <span className="label-text font-medium text-gray-700">
                    Policy Holder Date of Birth {requireDob && <span className="text-red-500">*</span>}
                  </span>
                </label>
                <DobField
                  key={String(isOpen)} // remount on open so unsaved typing is discarded like the other fields
                  value={policyHolderDob}
                  onChange={(iso, isValid) => {
                    setPolicyHolderDob(iso);
                    setPolicyHolderDobValid(isValid);
                  }}
                  borderClassName="border-gray-200 focus-within:border-teamcare-cyan"
                />
              </div>
            )}
          </div>

          {/* Member ID + Policy Holder SSN Row */}
          <div className={`grid grid-cols-1 ${showMemberId && showSsn ? 'md:grid-cols-2' : ''} gap-4`}>
            {/* Member ID - Conditional */}
            {showMemberId && (
              <div className="form-control w-full">
                <label className="label py-1">
                  <span className="label-text font-medium text-gray-700">
                    Member ID {requireMemberId && <span className="text-red-500">*</span>}
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Enter member ID"
                  className="input input-bordered w-full bg-white border-gray-200 focus:border-teamcare-cyan focus:outline-none"
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value)}
                />
              </div>
            )}
  
            {/* Policy Holder SSN - Conditional, masked, never persisted */}
            {showSsn && (
              <div className="form-control w-full">
                <label className="label py-1" htmlFor="policy-holder-ssn">
                  <span className="label-text font-medium text-gray-700">
                    Policy Holder SSN {requireSsn && <span className="text-red-500">*</span>}
                  </span>
                </label>
                <div className="relative">
                  <input
                    id="policy-holder-ssn"
                    name="policy-holder-ssn"
                    type={ssnVisible ? 'text' : 'password'}
                    autoComplete="off"
                    inputMode="numeric"
                    placeholder="XXX-XX-XXXX"
                    className={`input input-bordered w-full pr-10 bg-white focus:outline-none ${ssnError ? 'border-red-500' : 'border-gray-200 focus:border-teamcare-cyan'}`}
                    value={policyHolderSsn}
                    onChange={(e) => setPolicyHolderSsn(formatSsnInput(e.target.value))}
                  />
                  <button
                    type="button"
                    onClick={() => setSsnVisible((visible) => !visible)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                    aria-label={ssnVisible ? 'Hide SSN' : 'Show SSN'}
                  >
                    {ssnVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {ssnError && <p className="text-red-500 text-xs mt-1">{ssnError}</p>}
              </div>
            )}
          </div>

          {/* Warning Note - Conditional */}
          {showDisclaimer && disclaimerText && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <p className="text-amber-800 text-xs leading-relaxed">
                <span className="font-semibold">Disclaimer:</span> {disclaimerText}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 shrink-0 flex flex-col md:flex-row gap-3 md:justify-between">
          <Button title='Cancel' className='w-full md:w-72' variant='secondary' onClick={onClose} />
          <Button
            title='Save Insurance Details'
            className='w-full md:w-72'
            onClick={handleSave}
            disabled={!isFormValid()}
          />
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};
